import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';

import { PlusIcon, Trash2Icon } from 'lucide-react';
import { useFieldArray, useFormContext, useWatch, type FieldPath } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@/components/ui/input-group';
import { cn } from '@/lib/utils';

import { WebhookFormCommon } from './webhookTypes';

type HeaderTarget = 'name' | 'value';

type HeaderRow = { name?: string; value?: string };

const isHeaderRowComplete = (header?: HeaderRow): boolean =>
  typeof header?.name === 'string' &&
  header.name.trim() !== '' &&
  typeof header?.value === 'string' &&
  header.value.trim() !== '';

export const WebhookHeadersFieldArray = () => {
  const { t } = useTranslation();
  const { control, getValues, setFocus } = useFormContext<WebhookFormCommon>();
  const includeHeaders = useWatch({ control, name: 'includeHeaders' });
  const { fields, prepend, remove } = useFieldArray({ control, name: 'headers' });
  const fieldCount = fields.length;
  const [pendingFocusIndex, setPendingFocusIndex] = useState<number | null>(null);
  const prevIncludeHeadersRef = useRef<boolean>(includeHeaders);

  const focusHeaderField = useCallback(
    (index: number, target: HeaderTarget) => {
      if (index < 0) {
        return;
      }

      const fieldPath = `headers.${index}.${target}` as FieldPath<WebhookFormCommon>;
      setTimeout(() => {
        setFocus(fieldPath);
      }, 0);
    },
    [setFocus]
  );

  useEffect(() => {
    if (pendingFocusIndex === null) {
      return;
    }

    if (pendingFocusIndex > fieldCount - 1) {
      return;
    }

    focusHeaderField(pendingFocusIndex, 'name');
    setPendingFocusIndex(null);
  }, [fieldCount, focusHeaderField, pendingFocusIndex]);

  useEffect(() => {
    const wasDisabled = prevIncludeHeadersRef.current === false;
    const isNowEnabled = includeHeaders === true;

    if (wasDisabled && isNowEnabled && fields.length > 0) {
      focusHeaderField(0, 'name');
    }

    prevIncludeHeadersRef.current = includeHeaders;
  }, [includeHeaders, fields.length, focusHeaderField]);

  const handleRemoveRow = useCallback(
    (index: number) => {
      remove(index);
      const nextIndex = Math.min(index, fieldCount - 2);
      setPendingFocusIndex(Math.max(nextIndex, 0));
    },
    [fieldCount, remove]
  );

  const handleNameKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>, index: number) => {
      if (event.key !== 'Enter') {
        return;
      }

      event.preventDefault();
      focusHeaderField(index, 'value');
    },
    [focusHeaderField]
  );

  const handleValueKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>, index: number) => {
      if (event.key !== 'Enter') {
        return;
      }

      event.preventDefault();

      if (index !== 0) {
        focusHeaderField(index + 1, 'name');
        return;
      }

      const currentHeader = getValues('headers')?.[0];

      if (!currentHeader?.name?.trim()) {
        focusHeaderField(0, 'name');
        return;
      }

      if (!isHeaderRowComplete(currentHeader)) {
        return;
      }

      prepend({ name: '', value: '' });
      setPendingFocusIndex(0);
    },
    [prepend, focusHeaderField, getValues]
  );

  const handleAddHeader = useCallback(() => {
    prepend({ name: '', value: '' });
    setPendingFocusIndex(0);
  }, [prepend]);

  const firstHeader = useWatch({ control, name: 'headers.0' });
  const canAddHeader = isHeaderRowComplete(firstHeader);

  if (!includeHeaders) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      {fields.map((header, index) => (
        <div key={header.id} className="space-y-1">
          <div className="flex gap-2">
            <InputGroup className="flex-1 overflow-hidden">
              <FormField
                control={control}
                name={`headers.${index}.name`}
                render={({ field }) => (
                  <FormItem className="flex-1 space-y-0">
                    <FormLabel className="sr-only">header_key</FormLabel>
                    <FormControl>
                      <InputGroupInput
                        {...field}
                        placeholder={t('webhook.config.headers_name_placeholder')}
                        onKeyDown={(event) => handleNameKeyDown(event, index)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`headers.${index}.value`}
                render={({ field }) => (
                  <FormItem className="flex-1 space-y-0">
                    <FormLabel className="sr-only">header_value</FormLabel>
                    <FormControl>
                      <InputGroupInput
                        {...field}
                        placeholder={t('webhook.config.headers_value_placeholder')}
                        className="border-l border-stroke"
                        onKeyDown={(event) => handleValueKeyDown(event, index)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <InputGroupAddon align="inline-end" className="w-4 min-w-4 pr-6">
                <InputGroupButton
                  variant="ghost-neutral"
                  size="icon-xs"
                  className={cn(
                    'size-4 min-w-4',
                    index !== 0 && 'hover:text-destructive hover:[&_svg]:text-destructive'
                  )}
                  aria-label={index === 0 ? t('webhook.config.add_header') : t('common.delete')}
                  onClick={index === 0 ? handleAddHeader : () => handleRemoveRow(index)}
                  disabled={index === 0 && !canAddHeader}
                >
                  {index === 0 ? (
                    <PlusIcon className="max-w-4" />
                  ) : (
                    <Trash2Icon className="max-w-4" />
                  )}
                </InputGroupButton>
              </InputGroupAddon>
            </InputGroup>
          </div>
          <div className="flex gap-3">
            <FormField
              control={control}
              name={`headers.${index}.name`}
              render={() => (
                <FormItem className="flex-1">
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name={`headers.${index}.value`}
              render={() => (
                <FormItem className="flex-1">
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      ))}
    </div>
  );
};
